import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Heart, ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";

const MAX_ATTEMPTS = 8;
const POLL_MS = 2000;

export default function DonateSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", attempts: 0 });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", message: "No session id" });
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/stripe/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (!cancelled) setState({ phase: "paid", tier: data.tier, amount: data.amount_total / 100, currency: data.currency });
          return;
        }
        if (data.status === "expired") {
          if (!cancelled) setState({ phase: "expired" });
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          if (!cancelled) setState({ phase: "timeout" });
          return;
        }
        setTimeout(tick, POLL_MS);
      } catch {
        if (attempts >= MAX_ATTEMPTS) {
          if (!cancelled) setState({ phase: "timeout" });
        } else {
          setTimeout(tick, POLL_MS);
        }
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[var(--tt-bg)] flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-10 max-w-md w-full text-center" data-testid="donate-success-card">
          {state.phase === "polling" && (
            <>
              <Loader2 className="w-12 h-12 text-[var(--tt-primary)] mx-auto mb-4 animate-spin" />
              <h1 className="font-display text-2xl font-extrabold mb-2">Confirming your subscription…</h1>
              <p className="text-sm text-[var(--tt-text-muted)]">This usually takes a few seconds.</p>
            </>
          )}
          {state.phase === "paid" && (
            <>
              <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
              <h1 className="font-display text-2xl font-extrabold mb-2">You&apos;re in. <Heart className="inline w-5 h-5 text-[var(--tt-primary)] -mt-1" /></h1>
              <p className="text-sm text-[var(--tt-text-muted)] mb-5">
                {state.tier === "premium"
                  ? "Premium activated — downloads + streaming for 30 days. Massive thank-you."
                  : "TorHub TV activated — full Fire TV streaming for 30 days. Thanks for supporting!"}
              </p>
              <Link
                to={state.tier === "premium" ? "/downloads" : "/"}
                data-testid="donate-success-go"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white text-sm font-bold"
              >
                {state.tier === "premium" ? "Browse downloads" : "Start watching"} <ArrowRight className="w-4 h-4" />
              </Link>
            </>
          )}
          {(state.phase === "expired" || state.phase === "timeout" || state.phase === "error") && (
            <>
              <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <h1 className="font-display text-2xl font-extrabold mb-2">
                {state.phase === "expired" ? "Session expired" : state.phase === "timeout" ? "Still processing" : "Couldn't confirm"}
              </h1>
              <p className="text-sm text-[var(--tt-text-muted)] mb-5">
                {state.phase === "timeout"
                  ? "Stripe is taking longer than usual. If you completed payment, refresh in a minute — your tier will activate automatically."
                  : "Your card wasn't charged. Try again from the Downloads page."}
              </p>
              <Link
                to="/downloads"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/15 text-white text-sm font-bold"
              >
                Back to plans <ArrowRight className="w-4 h-4" />
              </Link>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
